
import React from 'react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Globe } from 'lucide-react';
import { cn } from '@/lib/utils';

interface LanguageSelectorProps {
  currentLanguage: string;
  onLanguageChange: (language: string) => void;
  variant?: "default" | "outline" | "secondary" | "ghost" | "link" | "minimal";
  className?: string;
  size?: "default" | "sm" | "lg" | "icon";
}

const languages = [
  { code: "en", name: "English" },
  { code: "hi", name: "हिंदी" },
  { code: "bn", name: "বাংলা" },
  { code: "te", name: "తెలుగు" },
  { code: "ta", name: "தமிழ்" },
  { code: "mr", name: "मराठी" },
  { code: "gu", name: "ગુજરાતી" },
  { code: "kn", name: "ಕನ್ನಡ" },
  { code: "ml", name: "മലയാളം" },
  { code: "pa", name: "ਪੰਜਾਬੀ" },
  { code: "or", name: "ଓଡିଆ" },
  { code: "all", name: "All Languages" },
];

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  currentLanguage,
  onLanguageChange,
  variant = "outline",
  className = "",
  size = "default"
}) => {
  const currentLang = languages.find(lang => lang.code === currentLanguage) || languages[0];
  
  // Handle the minimal variant specially
  const buttonVariant = variant === "minimal" ? "ghost" : variant;
  const isMinimalVariant = variant === "minimal";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant={buttonVariant}
          size={size} 
          className={cn(
            "flex items-center gap-2 transition-all hover:scale-[1.02]", 
            isMinimalVariant && "p-0.5 h-auto min-h-0 hover:bg-transparent",
            className
          )}
        >
          <Globe className={`${size === "sm" || isMinimalVariant ? "h-3.5 w-3.5" : "h-4 w-4"}`} />
          <span className={cn(
            size === "sm" ? "text-xs" : "",
            isMinimalVariant && "text-xs font-normal"
          )}>
            {currentLang.name}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-40 z-50 bg-white dark:bg-gray-800 shadow-md border-gray-200">
        {languages.map(lang => (
          <DropdownMenuItem 
            key={lang.code}
            className={cn(
              "text-sm cursor-pointer transition-colors",
              lang.code === currentLanguage ? "bg-accent/50 font-medium" : "hover:bg-accent/20"
            )}
            onClick={() => onLanguageChange(lang.code)}
          >
            {lang.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSelector;
